import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Hand } from "lucide-react";

/* Wordless, looping tutorial built from REAL screenshots of the service.
   An animated finger points at what to tap on each real screen. For clients
   who cannot read: real screens + numbers + a moving finger only. */

const SCENE_MS = 3600;

// real service screenshots (captured in Spanish) + where the finger points
const FRAMES = [
  { img: "/howto/1_phone.png", finger: { x: "50%", y: "70%" } }, // type phone number
  { img: "/howto/2_amount.png", finger: { x: "50%", y: "90%" } }, // enter amount + pay
  { img: "/howto/3_qr.png", finger: { x: "50%", y: "42%" } }, // scan QR with bank app
  { img: "/howto/4_success.png", finger: null }, // done
];

const Finger = ({ x, y, tapKey }) => (
  <motion.div
    className="absolute z-30 pointer-events-none"
    style={{ left: x, top: y, translateX: "-30%", translateY: "-10%" }}
  >
    <motion.div key={tapKey} animate={{ scale: [1, 0.8, 1] }} transition={{ duration: 0.9, repeat: Infinity, repeatDelay: 0.4 }} className="relative">
      <span className="absolute -left-3 -top-3 block h-12 w-12 rounded-full bg-[hsl(var(--primary))]/30 finger-ripple" />
      <Hand size={44} strokeWidth={2.2} className="text-slate-800 drop-shadow-[0_4px_8px_rgba(0,0,0,0.3)] rotate-12 fill-white" />
    </motion.div>
  </motion.div>
);

const StepDots = ({ scene }) => (
  <div className="flex items-center justify-center gap-2 mt-3" aria-hidden>
    {FRAMES.map((_, i) => (
      <motion.span
        key={i}
        animate={{
          width: scene === i ? 26 : 9,
          backgroundColor: scene === i ? "hsl(var(--primary))" : "rgba(148,163,184,0.5)",
        }}
        className="h-2.5 rounded-full"
      />
    ))}
  </div>
);

export const HowToAnimation = ({ compact = false }) => {
  const [scene, setScene] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setScene((s) => (s + 1) % FRAMES.length), SCENE_MS);
    return () => clearInterval(id);
  }, []);

  const frame = FRAMES[scene];
  const height = compact ? "h-[300px]" : "h-[440px]";
  const width = compact ? "max-w-[240px]" : "max-w-[300px]";

  return (
    <div className="flex flex-col items-center w-full" data-testid="howto-animation">
      <div className={`relative ${height} ${width} w-full mx-auto rounded-[2rem] border-4 border-slate-800 bg-white overflow-hidden shadow-[0_18px_50px_rgba(2,6,23,0.18)]`}>
        {/* phone notch */}
        <div className="absolute left-1/2 top-0 z-40 h-5 w-24 -translate-x-1/2 rounded-b-2xl bg-slate-800" />
        {/* step number badge */}
        <div className="absolute left-3 top-3 z-30 flex h-9 w-9 items-center justify-center rounded-full bg-[hsl(var(--primary))] text-white font-display font-bold text-lg shadow-md">
          {scene + 1}
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={scene}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0"
          >
            <img src={frame.img} alt="" className="h-full w-full object-cover object-top select-none" draggable={false} />
            {frame.finger && <Finger x={frame.finger.x} y={frame.finger.y} tapKey={scene} />}
          </motion.div>
        </AnimatePresence>
      </div>
      <StepDots scene={scene} />
    </div>
  );
};
